// Admin reporting endpoints (core-lambda) — Clients & Performance dashboards.
// Backend enforces small-n suppression; payloads are aggregate-only.

import { axiosAssessmentInstance } from "../AxiosConfiguration";

const unwrap = (response, fallback = null) =>
  response?.data?.payload ?? fallback;

// Repeat array params as ?demo=a&demo=b (FastAPI list style) instead of demo[].
const repeatArrays = { indexes: null };

// Predicate {gender:["Female"], age_band:["25-34","35-44"]} -> ["gender:Female", ...]
const demoPairs = (predicate) =>
  predicate
    ? Object.entries(predicate).flatMap(([dim, vals]) =>
        (vals || []).map((v) => `${dim}:${v}`)
      )
    : [];

/**
 * Clients (B2B) dashboard for one corporate.
 * @param {string} corporateId
 * @param {number} window 7 | 30 | 90 (days) — ignored when range is given
 * @param {Object|null} demoPredicate {dim:[values]} multi-select demographic filter
 * @param {{start:string,end:string}|null} range explicit ISO window
 */
export const getClientsDashboard = async (
  corporateId,
  window = 30,
  demoPredicate = null,
  range = null
) => {
  const params = { window };
  const demo = demoPairs(demoPredicate);
  if (demo.length) params.demo = demo;
  if (range?.start && range?.end) {
    params.start = range.start;
    params.end = range.end;
  }
  const response = await axiosAssessmentInstance.get(
    `/api/admin/enterprises/${corporateId}/dashboard`,
    { params, paramsSerializer: repeatArrays }
  );
  return unwrap(response, {});
};

/**
 * Global Performance dashboard.
 * @param {number} window 7 | 30 | 90 (days)
 * @param {{dimension:string,value:string}|null} filter single non-demographic
 *   dimension filter (session_type | model_version | prompt_version |
 *   entry_point | user_type).
 * @param {{start:string,end:string}|null} range explicit ISO window
 * @param {Object|null} demoPredicate {dim:[values]} multi-select demographic filter
 */
export const getPerformanceDashboard = async (
  window = 30,
  filter = null,
  range = null,
  demoPredicate = null
) => {
  const params = { window };
  if (filter?.dimension && filter?.value) {
    params.filter_dim = filter.dimension;
    params.filter_val = filter.value;
  }
  const demo = demoPairs(demoPredicate);
  if (demo.length) params.demo = demo;
  if (range?.start && range?.end) {
    params.start = range.start;
    params.end = range.end;
  }
  const response = await axiosAssessmentInstance.get(
    "/api/admin/reports/performance",
    { params, paramsSerializer: repeatArrays }
  );
  return unwrap(response, {});
};

// ── Cost metering (admin) ────────────────────────────────────────────────────
// LLM-spend reports + editable rate card. Money fields are USD floats; the
// backend has already split voice vs chat and applied the rate card. All report
// endpoints share the {window | start+end} time window (start+end overrides
// window). See admin_module/reporting/cost_controller.py for the contract.

// Build {window} or {window,start,end} plus any extra query params.
const costWindowParams = (window, range, extra = {}) => {
  const params = { window, ...extra };
  if (range?.start && range?.end) {
    params.start = range.start;
    params.end = range.end;
  }
  return params;
};

/**
 * Per-user spend roll-up (the main cost table). Sorted by total cost desc by
 * the backend; `summary` reflects the full filtered set, not just the page.
 * @param {number} window 7 | 30 | 90 (days) — ignored when range is given
 * @param {{start:string,end:string}|null} range explicit ISO window
 * @param {{corporateId?:string|null, limit?:number, offset?:number}} opts
 */
export const getUserCostReport = async (
  window = 30,
  range = null,
  { corporateId = null, limit = 100, offset = 0 } = {}
) => {
  const params = costWindowParams(window, range, { limit, offset });
  if (corporateId) params.corporate_id = corporateId;
  const response = await axiosAssessmentInstance.get("/api/admin/cost/users", {
    params,
  });
  return unwrap(response, {});
};

/**
 * One user, session by session (drill-down). Sessions are most-recent-first.
 * @param {string} userId
 * @param {number} window 7 | 30 | 90 (days)
 * @param {{start:string,end:string}|null} range explicit ISO window
 */
export const getUserCostDetail = async (userId, window = 30, range = null) => {
  const params = costWindowParams(window, range);
  const response = await axiosAssessmentInstance.get(
    `/api/admin/cost/users/${userId}`,
    { params }
  );
  return unwrap(response, {});
};

/**
 * Enterprise (org) spend roll-up — same shape as getUserCostReport, scoped to
 * one corporate's members.
 * @param {string} corporateId
 * @param {number} window 7 | 30 | 90 (days)
 * @param {{start:string,end:string}|null} range explicit ISO window
 * @param {{limit?:number, offset?:number}} opts
 */
export const getEnterpriseCostReport = async (
  corporateId,
  window = 30,
  range = null,
  { limit = 100, offset = 0 } = {}
) => {
  const params = costWindowParams(window, range, { limit, offset });
  const response = await axiosAssessmentInstance.get(
    `/api/admin/enterprises/${corporateId}/cost`,
    { params }
  );
  return unwrap(response, {});
};

/** List the model rate card (for the pricing admin screen). */
export const getCostPricing = async () => {
  const response = await axiosAssessmentInstance.get("/api/admin/cost/pricing");
  return unwrap(response, { count: 0, pricing: [] });
};

/**
 * Add or correct a model's rate. Returns {model, channel, created}.
 * @param {Object} body PricingUpsertBody — see contract §3.5. Required:
 *   {model, channel, rates}. Optional: verified, active, notes, source_url,
 *   effective_date.
 */
export const upsertCostPricing = async (body) => {
  const response = await axiosAssessmentInstance.put(
    "/api/admin/cost/pricing",
    body
  );
  return unwrap(response, {});
};
